import { useQuery } from 'react-query'
import api from 'balkerne-core/api'
import { METRIC_VIEWS, parseMetricKey } from './views'
import { CanonicalPeriod, MetricState } from '../../components/climateAnalytics/common'

const BASE_PATH = '/analytics/cc/metrics/data'

type PropertyId = number

export type PropertyResult = {
  property_id: PropertyId
  state: {
    property_id: PropertyId
    metric_id: number
    value: MetricState
    message: string | null
    detail: any | null
    created_at: string | null
    updated_at: string | null
  }
  data: any | null
}

type ClimateDataResponse = Record<PropertyId, PropertyResult>

type PropertyScope = { propertyId: number }
type PortfolioScope = { groupId?: number; propertyIds?: number[] }

type QueryParams = {
  emissionTiers?: string[] | null
  aggParams?: Record<string, any> | null
  facetQuery?: Record<string, any> | null
  canonicalPeriods?: CanonicalPeriod[] | null
  useFacetDefaults?: boolean | null
}

export function usePropertyClimateData(metricKey: string, scope: PropertyScope, params?: QueryParams) {
  const spec = METRIC_VIEWS[metricKey]
  if (!spec) throw new Error(`Unregistered property view for "${metricKey}"`)

  // Resolve params with precedence: params > spec defaults
  const canonicalPeriods =
    params && Object.prototype.hasOwnProperty.call(params, 'canonicalPeriods')
      ? params.canonicalPeriods
      : spec.canonical_periods ?? null
  const aggParams =
    params && Object.prototype.hasOwnProperty.call(params, 'aggParams') ? params.aggParams ?? {} : spec.agg_params ?? {}
  const facetQuery =
    params && Object.prototype.hasOwnProperty.call(params, 'facetQuery') ? params.facetQuery : spec.facet_query ?? null
  const useFacetDefaults =
    params && Object.prototype.hasOwnProperty.call(params, 'useFacetDefaults')
      ? params.useFacetDefaults ?? false
      : spec.use_facet_defaults

  const body = {
    metric_id: null,
    metric_key: parseMetricKey(metricKey),
    property_ids: [scope.propertyId],
    group_id: null,
    emission_tiers: params?.emissionTiers ?? null,
    canonical_periods: canonicalPeriods,
    use_facet_defaults: useFacetDefaults,
    facet_query: facetQuery,
    agg_params: aggParams,
  }

  const paramsKey = JSON.stringify({
    emissionTiers: body.emission_tiers,
    canonicalPeriods: body.canonical_periods,
    useFacetDefaults: body.use_facet_defaults,
    facetQuery: body.facet_query,
    aggParams: body.agg_params,
  })

  const queryKey = [BASE_PATH, 'property', metricKey, scope.propertyId, paramsKey]

  const fetch = async (): Promise<ClimateDataResponse> => {
    const res = await api.post<ClimateDataResponse>(BASE_PATH, body)
    return res.data
  }

  return useQuery<ClimateDataResponse, unknown, PropertyResult | undefined>(queryKey, fetch, {
    enabled: scope.propertyId != null && !isNaN(scope.propertyId),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: raw => raw?.[scope.propertyId] ?? undefined,
  })
}

export function usePortfolioClimateData(metricKey: string, scope?: PortfolioScope, params?: QueryParams) {
  const spec = METRIC_VIEWS[metricKey]
  if (!spec) throw new Error(`Unregistered portfolio view for "${metricKey}"`)

  // Resolve params with precedence: params > spec defaults
  const canonicalPeriods =
    params && Object.prototype.hasOwnProperty.call(params, 'canonicalPeriods')
      ? params.canonicalPeriods
      : spec.canonical_periods ?? null
  const aggParams =
    params && Object.prototype.hasOwnProperty.call(params, 'aggParams') ? params.aggParams ?? {} : spec.agg_params ?? {}
  const facetQuery =
    params && Object.prototype.hasOwnProperty.call(params, 'facetQuery') ? params.facetQuery : spec.facet_query ?? null
  const useFacetDefaults =
    params && Object.prototype.hasOwnProperty.call(params, 'useFacetDefaults')
      ? params.useFacetDefaults ?? false
      : spec.use_facet_defaults

  const body = {
    metric_id: null,
    metric_key: parseMetricKey(metricKey),
    property_ids: scope?.propertyIds ?? null,
    group_id: scope?.groupId ?? null,
    emission_tiers: params?.emissionTiers ?? null,
    canonical_periods: canonicalPeriods,
    use_facet_defaults: useFacetDefaults,
    facet_query: facetQuery,
    agg_params: aggParams,
  }

  const paramsKey = JSON.stringify({
    emissionTiers: body.emission_tiers,
    canonicalPeriods: body.canonical_periods,
    useFacetDefaults: body.use_facet_defaults,
    facetQuery: body.facet_query,
    aggParams: body.agg_params,
  })

  const queryKey = [
    BASE_PATH,
    'portfolio',
    metricKey,
    scope?.groupId ?? null,
    scope?.propertyIds ? scope.propertyIds.join(',') : null,
    paramsKey,
  ]
  const fetch = async (): Promise<ClimateDataResponse> => {
    const res = await api.post<ClimateDataResponse>(BASE_PATH, body)
    return res.data
  }

  return useQuery<ClimateDataResponse, unknown, ClimateDataResponse | undefined>(queryKey, fetch, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}
